import type { AdminCrewReportOwnerRepatriationBarSegmentProps } from "../../models/admin-crew-report.models";
import { getAdminCrewReportOwnerRepatriationSegmentWidth } from "../../utils/admin-crew-report-statistics.utils";
import type { CSSProperties } from "react";
import { useEffect, useRef, useState } from "react";

const AdminCrewReportOwnerRepatriationBarSegment = ({
  axisMaximum,
  count,
  ownerName,
  segment,
}: AdminCrewReportOwnerRepatriationBarSegmentProps) => {
  const segmentRef = useRef<HTMLSpanElement>(null);
  const labelRef = useRef<HTMLSpanElement>(null);
  const [doesLabelFit, setDoesLabelFit] = useState(false);

  useEffect(() => {
    const segmentElement = segmentRef.current;
    const labelElement = labelRef.current;
    if (!segmentElement || !labelElement) return undefined;

    const updateDoesLabelFit = () => {
      const nextDoesLabelFit = segmentElement.clientWidth > 0 && labelElement.scrollWidth <= segmentElement.clientWidth;
      setDoesLabelFit((currentDoesLabelFit) =>
        currentDoesLabelFit === nextDoesLabelFit ? currentDoesLabelFit : nextDoesLabelFit,
      );
    };

    updateDoesLabelFit();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateDoesLabelFit);
      return () => window.removeEventListener("resize", updateDoesLabelFit);
    }

    const resizeObserver = new ResizeObserver(updateDoesLabelFit);
    resizeObserver.observe(segmentElement);

    return () => resizeObserver.disconnect();
  }, [axisMaximum, count]);

  return (
    <span
      ref={segmentRef}
      title={`${segment.label}: ${count}`}
      data-test={`admin-crew-report-owner-repatriation-segment-${segment.key}`}
      data-testid={`admin-crew-report-owner-repatriation-segment-${ownerName}-${segment.key}`}
      className="relative z-10 flex h-5 min-w-0 items-center justify-center overflow-hidden whitespace-nowrap bg-[var(--admin-crew-report-segment-color)] text-[9px] font-semibold tabular-nums leading-none text-[var(--admin-crew-report-segment-label-color)] dark:bg-[var(--admin-crew-report-segment-dark-color)] dark:text-[var(--admin-crew-report-segment-dark-label-color)] sm:text-[10px]"
      style={
        {
          width: `${getAdminCrewReportOwnerRepatriationSegmentWidth(count, axisMaximum)}%`,
          "--admin-crew-report-segment-color": segment.color,
          "--admin-crew-report-segment-label-color": segment.labelColor,
          "--admin-crew-report-segment-dark-color": segment.darkColor,
          "--admin-crew-report-segment-dark-label-color": segment.darkLabelColor,
        } as CSSProperties
      }
    >
      <span
        ref={labelRef}
        data-testid={`admin-crew-report-owner-repatriation-segment-label-${ownerName}-${segment.key}`}
        className={`flex-none px-0.5 ${doesLabelFit ? "visible" : "invisible"}`}
      >
        {count}
      </span>
    </span>
  );
};

export default AdminCrewReportOwnerRepatriationBarSegment;
